import { useState } from "react";
import { MessageSquarePlus, Send, X, Star } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { trackEvent } from "@/lib/analytics";

interface Props {
  variant?: "inline" | "floating";
  page?: string;
  defaultOpen?: boolean;
}

const FeedbackForm = ({ variant = "floating", page, defaultOpen = false }: Props) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(defaultOpen || variant === "inline");
  const [recs, setRecs] = useState("");
  const [improve, setImprove] = useState("");
  const [rating, setRating] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!recs.trim() && !improve.trim() && !rating) {
      toast.error("Tell us at least one thing 🙏");
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.from("beta_feedback").insert({
      user_id: user?.id ?? null,
      recommendations_feedback: recs.trim() || null,
      improvement_feedback: improve.trim() || null,
      rating,
      page: page ?? (typeof window !== "undefined" ? window.location.pathname : null),
    });
    setSubmitting(false);
    if (error) return toast.error(error.message);
    await trackEvent("feedback_submitted", { rating });
    toast.success("Thanks for the feedback 💛");
    setRecs(""); setImprove(""); setRating(null);
    if (variant === "floating") setOpen(false);
  };

  const FormBody = (
    <div className="space-y-3">
      <div>
        <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          What did you think of your recommendations?
        </label>
        <Textarea rows={3} value={recs} onChange={(e) => setRecs(e.target.value)} placeholder="Were they on-point? Off?" className="mt-1" />
      </div>
      <div>
        <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          What would you improve?
        </label>
        <Textarea rows={3} value={improve} onChange={(e) => setImprove(e.target.value)} placeholder="Anything that would make this better." className="mt-1" />
      </div>
      <div>
        <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide block mb-1">
          Overall rating
        </label>
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setRating(rating === n ? null : n)}
              className={`p-1 transition-colors ${rating && n <= rating ? "text-amber-400" : "text-muted-foreground hover:text-amber-400/70"}`}
              aria-label={`${n} star${n > 1 ? "s" : ""}`}
            >
              <Star className="w-5 h-5" fill={rating && n <= rating ? "currentColor" : "none"} />
            </button>
          ))}
        </div>
      </div>
      <Button onClick={submit} disabled={submitting} className="w-full">
        <Send className="w-4 h-4 mr-1" /> Send feedback
      </Button>
    </div>
  );

  if (variant === "inline") {
    return <div className="glass rounded-2xl p-5">{FormBody}</div>;
  }

  return (
    <>
      {/* Floating trigger */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-40 bg-primary text-primary-foreground rounded-full px-4 py-3 text-sm font-semibold shadow-lg hover:opacity-90 inline-flex items-center gap-2"
        >
          <MessageSquarePlus className="w-4 h-4" /> Send feedback
        </button>
      )}

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-5 right-5 z-50 w-[min(92vw,380px)] glass rounded-2xl p-5 shadow-2xl border border-border"
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-bold text-base inline-flex items-center gap-2">
                <MessageSquarePlus className="w-4 h-4 text-primary" /> Send feedback
              </h3>
              <button onClick={() => setOpen(false)} aria-label="Close">
                <X className="w-4 h-4" />
              </button>
            </div>
            {FormBody}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default FeedbackForm;
